/**
 * Conversation Streaming Facade (100% Shared)
 * 
 * Wraps all streaming conversation-related state and actions.
 * This facade is completely platform-agnostic and can be used
 * by both web and plugin without any modifications.
 * 
 * Responsibilities:
 * - Streaming conversation creation
 * - Message submission
 * - Conversation history loading
 * - Real-time message updates
 * - Stream cancellation
 * 
 * Usage:
 * ```tsx
 * const streaming = useConversationStreamingFacade();
 * await streaming.createStreamingConversation({...});
 * ```
 * 
 * @module useConversationStreamingFacade
 * @category Facades/Shared
 */

import { useCallback, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  submitUserMessage,
  addAIResponse,
  setConversationId,
  updateAIMessage,
  updateMessageFeedback,
  setLoading,
  addMessage,
  setMessages,
  selectConversationState,
  initializeConversation,
  setInitiatedFromUncover,
} from '../../../store/slices/conversationSlice';
import { selectCurrentProjectId, getUserProfile } from '../../../store/slices/appSlice';
import { useAccessToken } from '../../useAccessToken';
import {
  createConversationStream,
  loadConversationHistory,
  conversationService,
} from '../../../api/services/conversation.service';
import type {
  ConversationHeaders,
  FirstChunkHeaders,
  StreamingConversationParams,
  Message,
  ConversationState,
  ConversationDetails,
} from '../../../types';

export interface ConversationStreamingFacade {
  // ========== State ==========
  /**
   * Current conversation state from Redux
   */
  conversationState: ConversationState;

  /**
   * Whether the conversation has encountered an error
   */
  hasError: boolean;

  /**
   * Whether currently loading history
   */
  isLoadingHistory: boolean;

  /**
   * Whether currently streaming a response
   */
  isStreaming: boolean;

  // ========== Actions ==========
  /**
   * Create a streaming conversation
   */
  createStreamingConversation: (
    message: string,
    existingConversationId?: string,
    onComplete?: (newConversationId?: string, headers?: ConversationHeaders) => void,
    setIsStreaming?: (streaming: boolean) => void,
    platform?: 'web' | 'plugin',
    attachmentIds?: string[],
    skipAnalysis?: boolean,
    isHiddenUserQuery?: boolean,
    actionType?: string,
    llmFamily?: string,
    modeOverride?: 'uncover' | 'prototyper',
  ) => Promise<void>;

  /**
   * Submit a message to the conversation
   */
  submitMessage: (text: string, attachmentIds?: string[]) => void;

  /**
   * Cancel ongoing streaming
   */
  cancelStreaming: () => void;

  /**
   * Load conversation history
   * @returns Promise with the loaded messages
   */
  loadHistory: (conversationId: string, projectId?: string | null) => Promise<Message[]>;

  /**
   * Fetch conversation details (title, project_id)
   */
  fetchConversationDetails: (conversationId: string) => Promise<ConversationDetails>;

  /**
   * Set error state
   */
  setHasError: (hasError: boolean) => void;

  /**
   * Send feedback (like/dislike) for a message
   */
  sendFeedback: (params: { messageID: string; isLiked: boolean; projectId?: string }) => Promise<void>;
}

/**
 * Conversation Streaming Facade Hook
 * 
 * Provides streaming conversation management capabilities.
 * 100% shared between web and plugin.
 */
export function useConversationStreamingFacade(): ConversationStreamingFacade {
  const dispatch = useAppDispatch();
  
  // State
  const conversationState = useAppSelector(selectConversationState);
  const currentProjectId = useAppSelector(selectCurrentProjectId);
  const accessToken = useAccessToken();
  const [hasError, setHasError] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isStreaming, setStreamingState] = useState(false);

  // Refs
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Create a streaming conversation with real-time updates
   */
  const createStreamingConversation = useCallback(
    async (
      message: string,
      existingConversationId?: string,
      onCompleteCallback?: (newConversationId?: string, headers?: ConversationHeaders) => void,
      setIsStreaming?: (streaming: boolean) => void,
      platform: 'web' | 'plugin' = 'web',
      attachmentIds?: string[],
      skipAnalysis?: boolean,
      isHiddenUserQuery?: boolean,
      actionType?: string,
      llmFamily?: string,
      modeOverride?: 'uncover' | 'prototyper',
    ) => {
      // Cancel any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      dispatch(setLoading(true));
      setStreamingState(true);
      setIsStreaming?.(true); // Also call external callback if provided

      const aiMessageId = (Date.now() + 1).toString();
      const initialAIMessage: Message = {
        id: aiMessageId,
        content: '',
        role: 'assistant',
      };
      dispatch(addMessage(initialAIMessage));

      let accumulatedContent = '';
      let firstChunkReceived = false;
      let detectedLanguage: 'rtl' | 'ltr' | undefined;
      let llmInteractionId: string | undefined;
      let storedConversationId: string | undefined;
      let storedHeaders: ConversationHeaders | undefined;
      let messageMode: 'uncover' | 'prototyper' | undefined;

      const streamParams: StreamingConversationParams = {
        session_id: conversationState.session_id || undefined,
        mode: modeOverride || conversationState.mode,
        user_message: message,
        project_id: currentProjectId || undefined,
        ...(existingConversationId && { conversation_id: existingConversationId }),
        ...(attachmentIds && attachmentIds.length > 0 && { attachments_ids: attachmentIds }),
        ...(skipAnalysis && { skip_analysis: skipAnalysis }),
        ...(isHiddenUserQuery && { is_hidden_user_query: isHiddenUserQuery }),
        ...(actionType && { action_type: actionType }),
        ...(platform && { platform_type: platform }),
        ...(llmFamily && { llm_family: llmFamily }),
      };

      await createConversationStream(
        streamParams,
        // onChunk
        (chunk: string, firstChunkHeaders?: FirstChunkHeaders, allHeaders?: ConversationHeaders) => {
          accumulatedContent += chunk;
          const cleanedContent = accumulatedContent.replace(/^[,\s\n\r]+/, '').trim();

          // Extract detected language and LLM interaction ID from first chunk headers
          if (!firstChunkReceived && firstChunkHeaders?.headers?.detectedLanguage) {
            detectedLanguage = firstChunkHeaders.headers.detectedLanguage;
          }
          if (!firstChunkReceived && firstChunkHeaders?.llmInteractionId) {
            llmInteractionId = firstChunkHeaders.llmInteractionId;
          }

          // Determine mode from headers when first chunk is received (if prototyper header is true, set mode to 'prototyper')
          if (!firstChunkReceived && allHeaders?.prototyper === true) {
            messageMode = 'prototyper';
          }

          dispatch(
            updateAIMessage({
              messageId: aiMessageId,
              content: cleanedContent,
              ...(detectedLanguage && { detectedLanguage }),
              ...(llmInteractionId && { llmInteractionId }),
              ...(messageMode && { mode: messageMode }),
            })
          );
          dispatch(setLoading(false));

          // Handle first chunk headers
          if (!firstChunkReceived && firstChunkHeaders) {
            setHasError(false);
            firstChunkReceived = true;
            
            // Store values for potential use in error handler
            storedConversationId = firstChunkHeaders.conversationId;
            storedHeaders = allHeaders;

            if (firstChunkHeaders.conversationId) {
              dispatch(setConversationId(firstChunkHeaders.conversationId));
            }

            // Call onComplete with headers from first chunk
            if (onCompleteCallback) {
              onCompleteCallback(firstChunkHeaders.conversationId, {repeated: allHeaders?.repeated});
            }
          }
        },
        // onError
        (error: Error) => {
          console.error('Streaming conversation error:', error);
          
          // Check if it's an abort error (user cancelled)
          if (error.name === 'AbortError') {
            // Don't show error for user-initiated cancellations
          } else {
            // Only update with error message if there's no accumulated content
            if (!accumulatedContent.trim()) {
              const errorStatus = (error as Error & { status?: number }).status;
              const errorMessage = error.message || 'Service unavailable. Please try again later.';
              const shouldShowErrorBanner = errorStatus === 403 && !!errorMessage;

              dispatch(
                updateAIMessage({
                  messageId: aiMessageId,
                  content: shouldShowErrorBanner ? errorMessage : 'Service unavailable. Please try again later.',
                  ...(shouldShowErrorBanner && {
                    hasBanner: true,
                    bannerType: 'error',
                    bannerPosition: 'bottom',
                    bannerText: errorMessage,
                  }),
                })
              );
              setHasError(true);
            } else {
              // Call onComplete when accumulatedContent has value with stored values
              onCompleteCallback?.(storedConversationId, storedHeaders);
            }
          }
          dispatch(setLoading(false));
          setStreamingState(false);
          setIsStreaming?.(false);
        },
        // onComplete
        (newConversationId?: string, headers?: ConversationHeaders) => {
          // Store values for potential use in error handler
          storedConversationId = newConversationId || storedConversationId;
          storedHeaders = headers || storedHeaders;
          
          // If mode wasn't set from first chunk but headers are available, set it now
          if (!messageMode && headers?.prototyper === true) {
            messageMode = 'prototyper';
            dispatch(
              updateAIMessage({
                messageId: aiMessageId,
                content: accumulatedContent.replace(/^[,\s\n\r]+/, '').trim(),
                ...(messageMode && { mode: messageMode }),
              })
            );
          }
          
          dispatch(setLoading(false));
          setStreamingState(false);
          setIsStreaming?.(false);

          if (newConversationId && !firstChunkReceived) {
            // Only set conversation ID if not already set from first chunk
            dispatch(setConversationId(newConversationId));
          }

          // Call the original onComplete callback from template
          if (firstChunkReceived) {
            // Headers were already processed from first chunk, just pass them through
            onCompleteCallback?.(newConversationId, headers);
          } else {
            // Fallback case - no first chunk received
            onCompleteCallback?.(newConversationId, headers);
          }

          // Silent refresh: Update user data (credits may have changed after streaming)
          // Fire-and-forget, silently fail if error
          if (accessToken) {
            dispatch(getUserProfile(accessToken)).catch(() => {
              // Silent failure - don't interrupt user flow
            });
          }
        },
        // signal - pass the abort signal
        abortControllerRef.current?.signal
      );
    },
    [dispatch, conversationState.session_id, conversationState.mode, currentProjectId]
  );

  /**
   * Submit a message to the conversation
   */
  const submitMessage = useCallback(
    (text: string, attachmentIds?: string[]) => {
      if (!text.trim() && (!attachmentIds || attachmentIds.length === 0)) return;
      dispatch(submitUserMessage({ text, attachmentIds }));
    },
    [dispatch]
  );

  /**
   * Cancel ongoing streaming
   */
  const cancelStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    dispatch(setLoading(false));
    setStreamingState(false);
  }, [dispatch]);

  /**
   * Load conversation history
   */
  const loadHistory = useCallback(
    async (conversationId: string, projectId?: string | null) => {
      setIsLoadingHistory(true);
      try {
        const messages = await loadConversationHistory(conversationId, projectId);
        // Convert API messages to Message format and add IDs
        const formattedMessages: Message[] = messages.map((msg, index) => ({
          id: msg.id,
          content: msg.content,
          role: msg.role,
          attachments_ids: msg.attachments_ids,
          detectedLanguage: msg.detectedLanguage === 'ar' ? 'rtl' : msg.detectedLanguage === 'en' ? 'ltr' : undefined,
          // Preserve header-related fields from API
          actionType: msg.actionType,
          nodeType: msg.nodeType,
          readyToGenerate: msg.readyToGenerate,
          mode: msg.mode,
          llmInteractionId: msg.llmInteractionId, // Preserve real backend UUID
          isLiked: msg.isLiked, // Preserve feedback state
          prototyper_demo_link_id: msg.prototyper_demo_link_id, // Preserve prototyper ID
          // Store additional metadata in message if needed
          ...(msg.nodeType && { drawType: msg.nodeType }),
        }));

        dispatch(setMessages(formattedMessages));
        
        // Set initiatedFromUncover flag based on first assistant message's mode
        const firstAssistantMessage = formattedMessages.find(msg => msg.role === 'assistant');
        if (firstAssistantMessage && firstAssistantMessage.mode === 'uncover') {
          dispatch(setInitiatedFromUncover(false));
        } else {
          dispatch(setInitiatedFromUncover(false));
        }
        
        return formattedMessages;
      } catch (error) {
        console.error('Failed to load conversation history:', error);
        throw error;
      } finally {
        setIsLoadingHistory(false);
      }
    },
    [dispatch]
  );

  /**
   * Fetch conversation details (title, project_id)
   */
  const fetchConversationDetails = useCallback(
    async (conversationId: string): Promise<ConversationDetails> => {
      try {
        const details = await conversationService.getConversationById(conversationId);
        return details;
      } catch (error) {
        console.error('Failed to fetch conversation details:', error);
        throw error;
      }
    },
    []
  );

  /**
   * Send feedback (like/dislike) for a message
   */
  const sendFeedback = useCallback(
    async (params: { messageID: string; isLiked: boolean; projectId?: string }): Promise<void> => {
      // Find message by llmInteractionId to get the actual message ID
      const message = conversationState.messages.find(
        (msg) => (msg as any).llmInteractionId === params.messageID || msg.id === params.messageID
      );

      // Store original state for potential rollback
      const originalIsLiked = message ? (message as any).isLiked : undefined;

      try {
        // Optimistically update UI
        if (message) {
          dispatch(updateMessageFeedback({
            messageId: message.id,
            isLiked: params.isLiked,
          }));
        }

        // Send to backend
        await conversationService.sendLlmInteractionFeedback(params);
      } catch (error) {
        console.error('Failed to send feedback:', error);
        
        // Revert optimistic update on error
        if (message) {
          dispatch(updateMessageFeedback({
            messageId: message.id,
            isLiked: originalIsLiked,
          }));
        }
        
        throw error;
      }
    },
    [conversationState.messages, dispatch]
  );

  return {
    // State
    conversationState,
    hasError,
    isLoadingHistory,
    isStreaming,

    // Actions
    createStreamingConversation,
    submitMessage,
    cancelStreaming,
    loadHistory,
    fetchConversationDetails,
    setHasError,
    sendFeedback,
  };
}
